import type { Agent, AgentCreate, AgentWithMemos } from './types';
import { createAuthenticatedFetch, type AssetExistsResponse } from './types/core/common';

// Interface pour les erreurs API
interface ApiError {
  status?: number;
  message?: string;
}

// @deprecated Use imports from '@/api/types' instead
export type {
  Agent,
  AgentCreate,
  AgentWithMemos
};

const api = createAuthenticatedFetch();

export const agents = {
  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================
  
  async fetchAllAgents(): Promise<Agent[]> {
    try {
      return await api.get('/agents/');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des agents");
    }
  },

  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les agents de l'utilisateur connecté avec leurs mémos
   */
  async getMy(): Promise<AgentWithMemos[]> {
    return api.get<AgentWithMemos[]>('/agents/my');
  },

  /**
   * Récupère les agents publics seulement avec leurs mémos
   */
  async getPublic(): Promise<AgentWithMemos[]> {
    return api.get<AgentWithMemos[]>('/agents/public');
  },

  /**
   * Récupère les agents accessibles (utilisateur + publics) avec leurs mémos
   */
  async getAccessible(): Promise<AgentWithMemos[]> {
    return api.get<AgentWithMemos[]>('/agents/accessible');
  },

  /**
   * Récupère tous les agents (admin seulement) avec leurs mémos
   */
  async getAllAdmin(): Promise<AgentWithMemos[]> {
    return api.get<AgentWithMemos[]>('/agents/all');
  },

  /**
   * Récupère tous les agents (admin seulement), y compris soft-deleted
   */
  async getAllAdminWithDeleted(): Promise<Agent[]> {
    return api.get<Agent[]>('/agents/all_with_deleted');
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  async createAgent(agent: AgentCreate): Promise<Agent> {
    return api.post<Agent>('/agents/', agent);
  },

  async updateAgent(uuid: string, agent: Partial<AgentCreate>): Promise<Agent> {
    return api.put<Agent>(`/agents/${uuid}`, agent);
  },

  async deleteAgent(uuid: string): Promise<void> {
    return api.delete(`/agents/${uuid}`);
  },

  async hardDeleteAgent(uuid: string): Promise<void> {
    return api.delete(`/agents/hard_delete/${uuid}`);
  },
  async restoreAgent(uuid: string): Promise<void> {
    return api.put(`/agents/restore/${uuid}`);
  },

  async fetchAgentsByUserId(user_id: string): Promise<Agent[]> {
    try {
      return await api.get(`/agents/user/${user_id}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Aucun agent trouvé pour cet user_id");
      }
      throw new Error("Erreur lors de la récupération des agents par user_id");
    }
  },

  async fetchAgentWithMemos(uuid: string): Promise<AgentWithMemos> {
    try {
      return await api.get(`/agents/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Agent non trouvé");
      }
      throw new Error("Erreur lors de la récupération de l'agent et ses mémos");
    }
  },

  async fetchPublicAgentWithMemos(uuid: string): Promise<AgentWithMemos> {
    try {
      return await api.get(`/agents/public/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Agent non trouvé");
      }
      throw new Error("Erreur lors de la récupération de l'agent et ses mémos");
    }
  },

  /**
   * Vérifie si un agent existe par son UUID (public ou privé)
   */
  async checkExists(uuid: string): Promise<AssetExistsResponse> {
    try {
      return await api.get(`/agents/exists/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        return {
          exists: false,
          is_owner: false,
          is_public: false
        };
      }
      throw new Error("Erreur lors de la vérification de l'existence de l'agent");
    }
  }
};

export const createAgent = agents.createAgent;
export const fetchAgentWithMemos = agents.fetchAgentWithMemos;